import { FC, memo, useCallback, useEffect, useRef, useState } from 'react'
import {
  DialogBase,
  ProgressCircle,
  Icon,
  Avatar,
  OverlayTrigger,
  Overlay,
  Toast,
  Tooltip,
  MenuItem,
  Theme,
  ActionMenu,
  Button
} from 'ui'
import { useHistoryState, useHistoryActions } from '@hooks/useHistory'
import { CANVAS_CONTAINER_ID } from '@services/engine/engine'
import {
  useIsProjectViewOnly,
  useProjectActions,
  useProjectState
} from '@hooks/useProject'
import { useIsMobileLayout } from '@hooks/useMobileLayout'
import styles from './HistoryDialog.module.scss'
import { useRouter } from 'next/router'
import CloseButton from '@components/closeButton/CloseButton'
import ClockPendingIcon from '/public/s2_icon_clock_pending_n.svg'
import ShareIcon from '/public/s2_icon_share.svg'
import { createNewProjectName, useCreateProject } from '@hooks/useCreateProject'
import { useCreateProjectWithDocumentMutation } from '@store/graphql/__generated__/schema'
import { getDocumentSnapshot } from '@hooks/useCopyProject'
import AdobeIMS from '@services/auth/IMS'
import { useAuth } from '@hooks/useAuth'
import { useTools } from '@services/engine/useTools'
import { ReportButton } from '@components/reportProject/ReportButton'
import UserIcon from '/public/s2_icon_user.svg'
import cn from 'classnames'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useTranslations } from 'use-intl'
import HistorySlider from './HistorySlider'
import { LocalStorageKey } from '@constants/localStorage'

const HistoryDialog: FC = () => {
  const engineState = useHistoryState('engineState')
  const isSceneLoading = useHistoryState('isSceneLoading')
  const showHistoryDialog = useHistoryState('showHistoryDialog')
  const { setShowHistoryDialog, setEngineState } = useHistoryActions()

  const projectUuid = useProjectState('projectUuid')
  const projectName = useProjectState('name')
  const projectOwner = useProjectState('owner')
  const createdAt = useProjectState('createdAt')
  const isFeatured = useProjectState('isFeatured')
  const isProjectViewOnly = useIsProjectViewOnly()

  const isMobileLayout = useIsMobileLayout()
  // Per side
  const dialogPadding = 60
  const creatorInfoHeight = 20
  const projectInfoHeight = 18
  // Per side
  const canvasContainerVerticalMargin = 16
  const sliderHeight = 18

  const canvasContainerMaxHeight = Math.min(
    700,
    document.documentElement.clientHeight -
      2 * dialogPadding -
      creatorInfoHeight -
      projectInfoHeight -
      2 * canvasContainerVerticalMargin -
      sliderHeight
  )

  const canvasContainerMaxWidth = Math.min(
    1000,
    document.documentElement.clientWidth - 2 * dialogPadding
  )

  const getCanvasContainerSize = (projectFrameSize: {
    width: number
    height: number
  }) => {
    const { width: frameWidth, height: frameHeight } = projectFrameSize
    const aspectRatio = frameWidth / frameHeight
    if (frameWidth > frameHeight) {
      const newHeight = Math.min(
        canvasContainerMaxHeight,
        canvasContainerMaxWidth / aspectRatio
      )

      return {
        width: newHeight * aspectRatio,
        height: newHeight
      }
    } else {
      const newWidth = Math.min(
        canvasContainerMaxWidth,
        canvasContainerMaxHeight * aspectRatio
      )

      return {
        width: newWidth,
        height: newWidth / aspectRatio
      }
    }
  }

  const projectFrameSize = useHistoryState('projectFrameSize')
  const creatorInfoRef = useRef<HTMLDivElement | null>(null)
  const canvasContainerRef = useRef<HTMLDivElement | null>(null)
  const sliderContainerRef = useRef<HTMLDivElement | null>(null)
  const dialogHeaderRef = useRef<HTMLDivElement | null>(null)
  const toastRef = useRef<HTMLDivElement | null>(null)

  const dialogRefs = useRef<React.RefObject<HTMLDivElement>[]>([
    creatorInfoRef,
    canvasContainerRef,
    sliderContainerRef,
    dialogHeaderRef,
    toastRef
  ])

  const { promptLogin, localUser } = useAuth()
  const flags = useFlags()
  const t = useTranslations()

  const showReportButton =
    flags['base-tf-ui-report-abuse'] && localUser?.uuid !== projectOwner?.uuid

  const { resetState } = useProjectActions()

  const showLoadingSpinner =
    isSceneLoading ||
    engineState === 'ERROR' ||
    !projectFrameSize ||
    !projectFrameSize

  const router = useRouter()

  const TOOL_ID = 'orbit'
  const ORBIT_TOOL_MODE = 0
  const { setPermanentToolID, permanentToolID } = useTools()

  const [createProject, { loading: isCreatingProject }] = useCreateProject({
    update: (_, mutationResult) => {
      const uuid = mutationResult.data?.createProject?.uuid
      if (uuid) {
        resetState()
        router.push(`/studio/${uuid}`)
      }
    },
    variables: {
      name: createNewProjectName(),
      tags: ['lifestyle', 'tech']
    }
  })

  const [createProjectWithDocumentMutation, { loading: isDuplicatingProject }] =
    useCreateProjectWithDocumentMutation({
      update: (_, mutationResult) => {
        const uuid = mutationResult.data?.createProjectWithDocument?.uuid
        if (!uuid) return

        closeDialog()
        resetState()
        router.push(`/studio/${uuid}`)
      }
    })

  const ref = useCallback(
    (node: HTMLDivElement) => {
      canvasContainerRef.current = node

      if (
        node &&
        (engineState === 'UNMOUNTED' || engineState === 'UNINITIALIZED') &&
        projectUuid
      ) {
        setEngineState('INITIALIZE')
      }
    },
    [showHistoryDialog.isOpen, engineState]
  )

  function closeDialog() {
    setEngineState('UNMOUNT')
    setShowHistoryDialog({
      isOpen: false
    })

    const historyRedirectUrl = localStorage.getItem(
      LocalStorageKey.historyRedirectUrl
    )
    if (historyRedirectUrl) {
      localStorage.removeItem(LocalStorageKey.historyRedirectUrl)
      router.push(historyRedirectUrl)
    }
  }

  const [isShareLinkToastOpen, setIsShareLinkToastOpen] = useState(false)

  function shareButtonClicked() {
    navigator.clipboard.writeText(
      `${window.location.origin}/discover/${projectUuid}`
    )

    setIsShareLinkToastOpen(true)
    setTimeout(() => {
      setIsShareLinkToastOpen(false)
    }, 6000)
  }

  function handleOnButtonClick() {
    if (AdobeIMS.isSignedInUser()) {
      handleConfirmAction()
    } else {
      promptLogin({
        onAuthenticated: handleConfirmAction,
        authenticationMethod: 'MODAL'
      })

      closeDialog()
    }
  }

  function handleConfirmAction() {
    if (isProjectViewOnly) {
      closeDialog()
      return createProject()
    }

    // TODO: replace duplicate with remix feature
    if (isFeatured) duplicateProject()
  }

  async function duplicateProject() {
    const documentSnapShot = await getDocumentSnapshot()
    if (!documentSnapShot) return

    createProjectWithDocumentMutation({
      variables: {
        name: `${projectName} (duplicate)`,
        document: documentSnapShot,
        tags: ['lifestyle', 'tech']
      }
    })
  }

  if (
    engineState === 'INITIALIZED_AND_DOCUMENT_LOADED' &&
    permanentToolID() !== TOOL_ID
  ) {
    setPermanentToolID(TOOL_ID, ORBIT_TOOL_MODE)
  }

  const handleUserNameClick = async () => {
    await router.push(`/profile/${projectOwner?.uuid}`)
    closeDialog()
  }

  const onDialogBaseMouseDown = (e: React.MouseEvent<HTMLElement>) => {
    // only care about left click
    if (e.button !== 0) return

    // Did the user click outside of the popup dialog?
    const outsideClick = dialogRefs.current.every(
      ref => !ref.current?.contains(e.target as Node)
    )

    if (!outsideClick) return
    closeDialog()
  }

  useEffect(() => {
    if (!showHistoryDialog.isOpen) {
      setEngineState('UNMOUNT')
    }

    setIsShareLinkToastOpen(false)
  }, [showHistoryDialog.isOpen])

  if (!showHistoryDialog.isOpen || !projectUuid || engineState === 'ERROR') {
    return null
  }

  let canvasContainerSize = null

  if (projectFrameSize) {
    canvasContainerSize = getCanvasContainerSize(projectFrameSize)
  }

  return (
    <DialogBase
      onMouseDown={onDialogBaseMouseDown}
      className={styles['dialog-base']}
      underlay
      mode="fullscreenTakeover"
      open={showHistoryDialog.isOpen}
      style={
        {
          '--canvas-container-width': canvasContainerSize?.width + 'px',
          '--canvas-container-height': canvasContainerSize?.height + 'px'
        } as React.CSSProperties
      }
    >
      <CloseButton className={styles['close-button']} onClick={closeDialog} />

      {showLoadingSpinner && (
        <div className={styles['progress-circle-container']}>
          <ProgressCircle size="l" indeterminate />
        </div>
      )}

      <div
        className={cn({
          [styles['dialog']]: true,
          [styles['scale-up']]: showHistoryDialog.isOpen && !showLoadingSpinner
        })}
        style={{
          padding: dialogPadding
        }}
      >
        <div ref={toastRef}>
          <Toast
            className={styles['toast']}
            variant="positive"
            open={isShareLinkToastOpen}
            close={() => {
              setIsShareLinkToastOpen(false)
            }}
          >
            {t('studio:shareMenu:copyLink:toastMessage')}
          </Toast>
        </div>

        <div ref={dialogHeaderRef} className={styles['dialog-header']}>
          <div
            ref={creatorInfoRef}
            className={styles['creator-info']}
            style={{
              gridTemplateRows: `${creatorInfoHeight}px ${projectInfoHeight}px`
            }}
          >
            {projectOwner?.adobeUserAvatarUrl && (
              <Avatar
                size={500}
                src={projectOwner.adobeUserAvatarUrl}
                label={
                  projectOwner?.adobeUserDisplayName ||
                  t('dashboard:historyDialog:defaultUsername')
                }
              />
            )}
            <OverlayTrigger placement="bottom" offset={0}>
              <h6
                className={styles['creator-name']}
                slot="trigger"
                onClick={handleUserNameClick}
              >
                {projectOwner?.adobeUserDisplayName ||
                  t('dashboard:historyDialog:defaultUsername')}
              </h6>

              <Tooltip slot="hover-content">
                {t('dashboard:historyDialog:moreFromUser')}
              </Tooltip>
            </OverlayTrigger>

            <div className={styles['project-description']}>
              <p className={styles['project-description-text']}>
                {projectName}
              </p>
              <div className={styles['project-description-date-container']}>
                <Icon size="xs">
                  <ClockPendingIcon />
                </Icon>
                <p>{createdAt}</p>
              </div>
            </div>
          </div>
          <div className={styles['dialog-header__spacer']}></div>
          <Button
            id="history-dialog-share-btn"
            className={styles['share-btn']}
            variant="accent"
            size="m"
            onClick={shareButtonClicked}
            style={{
              whiteSpace: 'nowrap'
            }}
          >
            <Icon slot="icon">
              <ShareIcon />
            </Icon>
            Share
          </Button>
        </div>

        <div
          className={styles['canvas-container']}
          id={CANVAS_CONTAINER_ID}
          ref={ref}
          style={{
            margin: `${canvasContainerVerticalMargin}px 0`
          }}
        >
          <div className={styles['action-menu-container']}>
            <>
              <Theme theme="express" color="light">
                <ActionMenu className={cn(styles['action-menu'])}>
                  <MenuItem onClick={handleUserNameClick}>
                    <Icon slot="icon" className="icon-m">
                      <UserIcon />
                    </Icon>
                    {t('dashboard:historyDialog:moreFromUser')}
                  </MenuItem>
                  {showReportButton && projectOwner?.adobeUserId && (
                    <ReportButton
                      projectUuid={projectUuid}
                      ownerAdobeUserId={projectOwner.adobeUserId}
                    />
                  )}
                </ActionMenu>
              </Theme>
            </>
          </div>
        </div>

        <HistorySlider
          handleOnButtonClick={handleOnButtonClick}
          isProjectViewOnly={isProjectViewOnly}
          isFeatured={isFeatured}
          engineState={engineState}
          isCreatingProject={isCreatingProject}
          isDuplicatingProject={isDuplicatingProject}
          sliderRef={sliderContainerRef}
          height={sliderHeight}
          isMobileLayout={isMobileLayout}
        />
      </div>
    </DialogBase>
  )
}

export default memo(HistoryDialog)
